//changed due to package upgrade
import React, { useState, useEffect } from 'react';
import { useAuthUser } from '@projectK/hooks/AuthHooks';
import { Formik, Form } from 'formik';
import * as yup from 'yup';
import PropTypes from 'prop-types';
import { alpha, Box, Button, Typography } from '@mui/material';
import AppGridContainer from '@projectK/components/AppGridContainer';
import Grid from '@mui/material/Grid';
import IntlMessages from '@projectK/helpers/IntlMessages';
import { useInfoViewActionsContext } from '@projectK/context/InfoViewContextProvider';
import jwtAxios from '@projectK/services/auth/JWT';
import { Fonts } from '@projectK/constants/AppEnums';
import AppTextField from '@projectK/components/AppTextField';
import AppInfoView from '@projectK/components/AppInfoView';
import CompanyTheme from '@projectK/constants/CompanyTheme';

const validationSchema = yup.object({
  jersey_size: yup
    .string()
    .matches(/^[A-Za-z0-9]+$/, 'Please enter valid jersey size.')
    .nullable()
    .required('Jersey size is required.'),
});

const ApparelInfo = ({ fetchData, onClose }) => {
  const companyTheme = CompanyTheme();
  const infoViewActionsContext = useInfoViewActionsContext();
  const { user } = useAuthUser();
  const [settings, setSettings] = useState(null);

  const fetchSettings = async () => {
    try {
      const response = await jwtAxios.get(`/users/${user.id}`);
      setSettings(response.data);
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  return (
    <>
      <Box>
        <Formik
          enableReinitialize={true}
          validateOnBlur={true}
          initialValues={{
            jersey_size: settings?.jersey_size || '',
          }}
          validationSchema={validationSchema}
          onSubmit={(values, { setSubmitting }) => {
            setSubmitting(true);
            jwtAxios
              .put(`/users/${user.id}`, values)
              .then((response) => {
                setSubmitting(false);
                fetchSettings();
                infoViewActionsContext.showMessage(
                  'Apparel Information Updated Successfully',
                );
              })
              .catch((error) => {
                setSubmitting(false);
                infoViewActionsContext.fetchError(error.message);
              });
          }}
        >
          {({ values, setFieldValue, handleChange, resetForm }) => {
            return (
              <Form noValidate autoComplete='off'>
                <Typography
                  component='h3'
                  sx={{
                    fontSize: 16,
                    fontWeight: Fonts.BOLD,
                    color: companyTheme
                      ? companyTheme.primary.main
                      : 'text.primary',
                  }}
                >
                  <IntlMessages id='common.apparelInfo' />
                </Typography>
                <Grid size={{ xs: 12, md: 12 }}>
                  <AppGridContainer sx={{ mt: 2 }}>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <AppTextField
                        name='jersey_size'
                        value={values.jersey_size}
                        variant='outlined'
                        sx={{
                          width: '100%',
                        }}
                        label='Jersey Size'
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 12 }}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                        }}
                      >
                        <Button
                          sx={{
                            position: 'relative',
                            minWidth: 100,
                            backgroundColor: companyTheme
                              ? companyTheme.primary.main
                              : '#2196f3',
                            '&:hover': {
                              backgroundColor: companyTheme
                                ? alpha(companyTheme.primary.main, 0.8) // Adjust the alpha value as needed
                                : alpha('#2196f3', 0.8),
                            },
                          }}
                          variant='contained'
                          type='submit'
                        >
                          Save
                        </Button>

                        <Button
                          sx={{
                            position: 'relative',
                            minWidth: 100,
                            ml: 2.5,
                            borderColor: companyTheme
                              ? companyTheme.primary.main
                              : '#2196f3',
                            '&:hover': {
                              borderColor: companyTheme
                                ? alpha(companyTheme.primary.main, 0.8)
                                : alpha('#2196f3', 0.8),
                            },
                            color: companyTheme
                              ? companyTheme.primary.main
                              : '#2196f3',
                          }}
                          variant='outlined'
                          onClick={(e) => {
                            e.preventDefault();
                            // Reset the form back to its initial state
                            resetForm();
                          }}
                        >
                          Cancel
                        </Button>
                      </Box>
                    </Grid>
                  </AppGridContainer>
                </Grid>
              </Form>
            );
          }}
        </Formik>
      </Box>
      <AppInfoView />
    </>
  );
};

export default ApparelInfo;

ApparelInfo.propTypes = {
  fetchData: PropTypes.func,
  onClose: PropTypes.func,
};
